import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  RefreshCw,
  Clock,
  CreditCard,
  Banknote,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { useAdminDashboardKpis, useTransactionReport } from "@/hooks/useDashboardData";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Tooltip, Legend, Title } from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title
);

/**
 * Dashboard de Administrador
 * Muestra KPIs clave, estadísticas de ventas y transacciones
 * Optimizado con React Query para caching automático
 */
const AdminDashboardPage: React.FC = () => {
  const { toast } = useToast();

  // Usar React Query hooks con caching automático
  const {
    data: kpis,
    isLoading: isLoadingKpis,
    error: kpisError,
    refetch: refetchKpis,
  } = useAdminDashboardKpis();

  const {
    data: transactionReport,
    isLoading: isLoadingTransactions,
    error: transactionsError,
    refetch: refetchTransactions,
  } = useTransactionReport(10, 1);

  // Combinar loading states
  const isLoading = isLoadingKpis || isLoadingTransactions;

  // Manejar errores de KPIs
  React.useEffect(() => {
    if (kpisError) {
      const error = kpisError as any;
      console.error('Error loading KPIs:', error);
      if (error.response?.status === 401) {
        toast({
          title: "Sesión Expirada",
          description: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
          variant: "destructive",
        });
      } else if (error.response?.status === 403) {
        toast({
          title: "Sin Permisos",
          description: "No tienes permisos para acceder a esta información.",
          variant: "destructive",
        });
      }
    }
  }, [kpisError, toast]);

  // Manejar errores de transacciones
  React.useEffect(() => {
    if (transactionsError) {
      const error = transactionsError as any;
      console.error('Error loading transactions:', error);
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        toast({
          title: "Error en Transacciones",
          description: "No se pudieron cargar las transacciones",
          variant: "destructive",
        });
      }
    }
  }, [transactionsError, toast]);

  const handleRefresh = async () => {
    await Promise.all([refetchKpis(), refetchTransactions()]);
    toast({
      title: "Datos actualizados",
      description: "El dashboard se ha actualizado con la información más reciente",
    });
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    });
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: es });
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "EFECTIVO":
        return <Banknote className="h-4 w-4 text-green-600" />;
      case "TARJETA":
        return <CreditCard className="h-4 w-4 text-blue-600" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  // Datos y opciones de gráficos

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } }
  };



  const topProductsData = React.useMemo(() => {
    const top = (kpis?.topSellingProducts ?? []).slice(0, 5);
    return {
      labels: top.map((p) => p.productName),
      datasets: [
        {
          label: "Unidades vendidas",
          data: top.map((p) => p.totalSold),
          backgroundColor: "#6366f1"
        }
      ]
    };
  }, [kpis]);

  const transactionsBarData = React.useMemo(() => {
    const list = (transactionReport?.transacciones ?? []).slice(0, 7);
    return {
      labels: list.map((t) => t.clienteNombre ?? "Cliente"),
      datasets: [
        {
          label: "Monto",
          data: list.map((t) => t.monto),
          backgroundColor: "#10b981"
        }
      ]
    };
  }, [transactionReport]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            Dashboard Administrativo
          </h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
            Visión general del rendimiento comercial y operativo
          </p>
          {kpis && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Actualizado: {formatDate(kpis.generatedAt)}
            </p>
          )}
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          size="sm"
          disabled={isLoading}
          className="h-10 w-full sm:w-auto"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
          />
          Actualizar
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && !kpis && (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Cargando datos del dashboard...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && !kpis && (
        <div className="flex justify-center items-center py-12">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <span>Error al cargar dashboard</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                No se pudieron cargar los datos del dashboard. Esto puede deberse a:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Problemas de conexión con el servidor</li>
                <li>Sesión expirada (intenta iniciar sesión nuevamente)</li>
                <li>Permisos insuficientes</li>
              </ul>
              <Button onClick={handleRefresh} className="mt-4 w-full">
                Reintentar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dashboard Content - Solo mostrar si hay datos */}
      {kpis && (
        <>
          {/* KPIs de Ventas */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Ventas y Pedidos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
              {/* Ventas de Hoy */}
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Ventas de Hoy</CardTitle>
                  <DollarSign className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl sm:text-2xl font-bold">
                    {formatCurrency(kpis.totalSalesToday)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Semana: {formatCurrency(kpis.totalSalesThisWeek)}
                  </p>
                </CardContent>
              </Card>

              {/* Pedidos de Hoy */}
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pedidos de Hoy</CardTitle>
                  <ShoppingCart className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl sm:text-2xl font-bold">
                    {kpis.totalOrdersToday}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Semana: {kpis.totalOrdersThisWeek}
                  </p>
                </CardContent>
              </Card>

              {/* Ticket Promedio */}
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Ticket Promedio</CardTitle>
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl sm:text-2xl font-bold">
                    {formatCurrency(kpis.averageOrderValue)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tiempo prep: {kpis.averagePreparationTime}
                  </p>
                </CardContent>
              </Card>

              {/* Hora Pico */}
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Hora Pico</CardTitle>
                  <Clock className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-xl sm:text-2xl font-bold">
                    {kpis.peakHour}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Mayor actividad
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

      {/* Productos Más Vendidos */}
      {kpis && kpis.topSellingProducts && kpis.topSellingProducts.length > 0 && (
        <div>
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Productos Más Vendidos</h2>
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-sm sm:text-base font-medium">Top 5 por unidades</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-72 w-full">
                <Bar data={topProductsData} options={barOptions} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Estados de Pedidos */}
      {/* <div>
        <h2 className="text-lg font-semibold mb-4">Estado de Pedidos</h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Distribución de estados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Doughnut data={orderStatusData} options={doughnutOptions} />
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Métodos de Pago y Usuarios */}
      {/* <div>
        <h2 className="text-lg font-semibold mb-4">Métodos de Pago y Usuarios</h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Métodos de pago (Hoy)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <Doughnut data={paymentMethodData} options={doughnutOptions} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Usuarios Activos</CardTitle>
              <Users className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : kpis ? kpis.totalActiveUsers : "0"}
              </div>
              <p className="text-xs text-muted-foreground">
                Nuevos hoy: {kpis ? kpis.newUsersToday : "0"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Entregas QR</CardTitle>
              <QrCode className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : kpis ? kpis.totalQRDeliveriesToday : "0"}
              </div>
              <p className="text-xs text-muted-foreground">
                Semana: {kpis ? kpis.totalQRDeliveriesThisWeek : "0"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div> */}



      {/* Últimas Transacciones */}
      {transactionReport && transactionReport.transacciones && transactionReport.transacciones.length > 0 && (
        <div>
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Últimas Transacciones</h2>
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-sm sm:text-base">
                Resumen de Transacciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/10 rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-400">
                    {formatCurrency(transactionReport.estadisticas.montoTotal)}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Transacciones</p>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-400">
                    {transactionReport.estadisticas.totalTransacciones}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Cantidad</p>
                </div>
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
                  <div className="text-lg sm:text-2xl font-bold text-purple-700 dark:text-purple-400">
                    {formatCurrency(transactionReport.estadisticas.promedioTransaccion)}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Promedio</p>
                </div>
              </div>

              <div className="h-48 sm:h-64 mb-4 sm:mb-6 w-full">
                <Bar data={transactionsBarData} options={barOptions} />
              </div>
              
              <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
                {transactionReport.transacciones.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                    <div className="flex items-center space-x-3 mb-2 sm:mb-0">
                      {getPaymentMethodIcon(transaction.metodoPago)}
                      <div>
                        <div className="font-medium text-sm sm:text-base">{transaction.clienteNombre}</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">
                          {formatDate(transaction.fecha)}
                        </div>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="font-medium text-sm sm:text-base">{formatCurrency(transaction.monto)}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground">{transaction.metodoPago}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Información del Período */}
      {kpis && kpis.period && (
        <div>
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-sm sm:text-base">Información del Período</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="font-medium">Hoy:</span> {kpis.period.today}
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="font-medium">Inicio de Semana:</span> {kpis.period.weekStart}
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="font-medium">Inicio de Mes:</span> {kpis.period.monthStart}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;
